import { useEffect, useState, useMemo } from "react"
import { Link, useParams } from "react-router-dom"
import AddToListModal from "../Account_Panels/User/List/AddtoListModal"
import CartUpdater from "../context/cartUpdater"
import { formatPurchaseDate } from "../util/purchaseDate"

const currency = new Intl.NumberFormat("en-PH", {
  style: "currency",
  currency: "PHP",
})

const Product = () => {
  const { id: product_id } = useParams()

  const [product, setProduct] = useState(null)
  const [qty, setQty] = useState(1)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(false)

  const unitPrice = Number(product?.price || 0)
  const totalPrice = useMemo(() => currency.format(unitPrice * qty), [unitPrice, qty])

  const setQuantity = (n) => setQty(Math.min(9999, Math.max(1, Math.floor(Number(n)) || 1)))

  useEffect(() => {
    if (!product_id) return

    const controller = new AbortController()

    const load = async () => {
      try {
        setLoading(true)
        setError(false)

        const res = await fetch(`${process.env.REACT_APP_API_URL}/products/${product_id}`, {
          signal: controller.signal,
        })

        if (!res.ok) throw new Error("Product fetch failed")

        setProduct(await res.json())
      } catch (err) {
        if (err.name !== "AbortError") {
          console.error(err)
          setError(true)
        }
      } finally {
        setLoading(false)
      }
    }

    load()
    return () => controller.abort()
  }, [product_id])

  if (loading)
    return (
      <div className="text-center py-5">
        <div className="spinner-border text-success" role="status">
          <span className="visually-hidden">Loading…</span>
        </div>
      </div>
    )

  if (error || !product)
    return (
      <div className="text-center py-5 px-3">
        <h1 className="h4">We couldn't load this product</h1>
        <Link to="/products" className="btn btn-outline-success mt-3">
          Back to products
        </Link>
      </div>
    )

  const moq = Number(product.min_qty || 0)

  return (
    <div className="Product container py-3 py-md-5 px-3 text-start" style={{ maxWidth: 1100 }}>
      <Link to="/products" className="d-inline-block mb-3 text-success text-decoration-none">
        ‹ All products
      </Link>

      <div className="row g-4">
        {/* IMAGE */}
        <div className="col-md-6">
          <img
            src={product.image}
            className="w-100 rounded-3"
            style={{ aspectRatio: "1 / 1", objectFit: "cover", background: "#f8f9fa" }}
            alt={product.name}
          />
        </div>

        {/* INFO */}
        <div className="col-md-6 d-flex flex-column">
          <div className="text-muted small mb-1">
            {product.category}
            {product.subCategory ? ` › ${product.subCategory}` : ""}
          </div>
          <h1 className="h3 mb-2">{product.name}</h1>

          <div className="fs-3 fw-bold mb-1">
            {currency.format(unitPrice)}
            <span className="fs-6 fw-normal text-muted"> / case</span>
          </div>

          <ul className="list-unstyled small text-muted mb-3">
            {moq > 0 && (
              <li>
                Minimum group order: <strong className="text-body">{moq} cases</strong>
              </li>
            )}
            <li>
              Next purchase date: <strong className="text-body">{formatPurchaseDate(product.category)}</strong>
            </li>
          </ul>

          {product.description && <p className="mb-4">{product.description}</p>}

          {/* QUANTITY */}
          <label htmlFor="qty" className="form-label fw-semibold mb-1">
            Quantity (cases)
          </label>
          <div className="d-flex align-items-center gap-3 mb-3">
            <div className="input-group" style={{ width: "9.5rem" }}>
              <button
                type="button"
                className="btn btn-outline-secondary"
                onClick={() => setQuantity(qty - 1)}
                disabled={qty <= 1}
                aria-label="One less case"
              >
                −
              </button>
              <input
                id="qty"
                type="number"
                inputMode="numeric"
                min="1"
                className="form-control text-center"
                value={qty}
                onChange={(e) => setQuantity(e.target.value)}
              />
              <button
                type="button"
                className="btn btn-outline-secondary"
                onClick={() => setQuantity(qty + 1)}
                aria-label="One more case"
              >
                +
              </button>
            </div>
            <div>
              <div className="small text-muted">Total</div>
              <div className="fs-5 fw-semibold">{totalPrice}</div>
            </div>
          </div>

          <div className="d-grid gap-2" style={{ maxWidth: 420 }}>
            <CartUpdater
              classes="btn-success btn-lg"
              item={{
                product_id,
                name: product.name,
                price: product.price,
                qty,
                image: product.image,
              }}
            />
            <button
              className="btn btn-outline-success"
              data-bs-toggle="modal"
              data-bs-target="#addToListModal"
            >
              Add to list
            </button>
          </div>
        </div>
      </div>

      <AddToListModal product={product} />
    </div>
  )
}

export default Product
